import { makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { MicrosoftLogo } from './MicrosoftLogo';

const useStyles = makeStyles({
  // Figma V4.6 is a fixed 1600px canvas with no wide-screen spec. Cap the
  // shell at that width and center it so the composition (left text column,
  // right-anchored hero, bottom-anchored actions — all positioned against
  // this shell) keeps its designed relationship instead of stretching apart
  // on wider viewports. At <=1600px the cap is inert and nothing changes;
  // pixel-verification against the design exports must therefore run at the
  // 1600x1000 reference viewport. The side margins beyond the cap stay
  // seamless because the FluentProvider root paints colorNeutralBackground1
  // across the full body width (body css matches as a second layer).
  shell: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    maxWidth: '1600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    paddingLeft: '227px',
    paddingRight: '32px',
    '@media (max-width: 1280px)': {
      paddingLeft: '96px',
    },
    '@media (max-width: 768px)': {
      paddingLeft: '32px',
      paddingTop: '72px',
    },
  },
  logo: {
    position: 'absolute',
    left: '34px',
    top: '35px',
    lineHeight: 0,
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    // Programmatically focusable as the skip-link target — no visible ring.
    outlineStyle: 'none',
  },
  footer: {
    alignSelf: 'flex-end',
    textAlign: 'left',
    marginTop: '12px',
    marginBottom: '30px',
    color: tokens.colorNeutralForeground4,
    fontSize: '12px',
    lineHeight: '16px',
  },
  skipLink: {
    position: 'absolute',
    left: '-9999px',
    top: '0',
    zIndex: 100,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForegroundLink,
    padding: '8px 16px',
    ':focus': {
      left: '0',
    },
  },
});

/**
 * Shared page shell per Figma V4.6: Microsoft logo in the left gutter,
 * left-anchored content column (240px gutter, 32px right inset), and the
 * "Powered by" attribution bottom-right on every screen. The whole shell is
 * capped at the 1600px design width and centered on wider viewports.
 */
export function AppShell({ children }: { children: React.ReactNode }): React.ReactElement {
  const styles = useStyles();
  const { t } = useTranslation('aita');

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main-content">
        {t('chrome.skip_to_content', 'Skip to main content')}
      </a>
      <header className={styles.logo}>
        <MicrosoftLogo />
      </header>
      {/* tabIndex makes the fragment target reliably take focus (Safari and
          older engines don't move focus to non-focusable fragment targets). */}
      <main id="main-content" className={styles.main} tabIndex={-1}>
        {children}
      </main>
      <footer className={styles.footer}>
        <div>{t('chrome.powered_by', 'Powered by:')}</div>
        <div>{t('chrome.powered_by_org', 'AI Business Solutions & Security')}</div>
        <div>{t('chrome.powered_by_team', 'Partner eXperience (ASPX)')}</div>
      </footer>
    </div>
  );
}
